import React, { useEffect, useState, useRef } from "react";
import { useLocale } from "../i18n";
import { useConcierge } from "../lib/concierge";
import { MessageBody } from "../components/concierge/MessageBody";
import { TripPanel, CostCard, ChatError } from "../components/concierge/TripPanel";
import { Send, Loader2, Sparkles } from "lucide-react";
import SEO, { buildBreadcrumb } from "../components/SEO";

export default function ConciergePage() {
  const { locale, t } = useLocale();
  const { messages, streaming, send: sendMsg } = useConcierge();
  const [input, setInput] = useState("");
  const [handoffNotice, setHandoffNotice] = useState(false);
  const scrollRef = useRef(null);

  // WOW Journey handoff: prefill the input with a user-facing summary.
  // We never auto-send — the visitor must review and press Send themselves.
  useEffect(() => {
    try {
      const prefill = window.sessionStorage.getItem("wj_concierge_prefill");
      if (prefill) {
        setInput(prefill);
        setHandoffNotice(true);
        window.sessionStorage.removeItem("wj_concierge_prefill");
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = (msgText) => {
    const text = (msgText ?? input).trim();
    if (!text) return;
    sendMsg(text, locale);
    setInput("");
    setHandoffNotice(false);
  };

  const starters = locale === "ro" ? t("concierge.starters_ro") : t("concierge.starters_en");

  return (
    <div className="min-h-[calc(100vh-73px)] bg-[var(--wr-night)] text-[var(--wr-bg)]" data-testid="concierge-page">
      <SEO
        title={locale === "ro" ? "Concierge pentru călătoriile în România" : "Concierge for travelling in Romania"}
        description={locale === "ro"
          ? "Întreabă orice despre călătoriile în România — istorie, rute, mâncare, logistică. Bilingv română și engleză."
          : "Ask anything about travelling in Romania — history, routes, food, logistics. Bilingual Romanian and English."}
        path="/concierge"
        jsonLd={[buildBreadcrumb([
          { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
          { name: "Concierge", path: "/concierge" },
        ])]}
      />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-20 flex flex-col min-h-[calc(100vh-73px)]">
        <div className="mb-10">
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4 flex items-center gap-2">
            <Sparkles size={12} /> CONCIERGE
          </p>
          <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {t("concierge.heading")}
          </h1>
          <p className="mt-5 text-white/70 text-[16px] max-w-2xl leading-relaxed">
            {t("concierge.sub")}
          </p>
        </div>

        {messages.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-3 mb-10" data-testid="starter-grid">
            {starters.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`concierge-page-starter-${i}`}
                className="text-left text-[14px] px-5 py-4 border border-white/10 hover:border-[var(--wr-red)] rounded-sm transition-colors text-white/85"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div ref={scrollRef} className="flex-1 space-y-5 mb-8 overflow-y-auto" data-testid="concierge-page-messages">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-md px-5 py-4 text-[15px] leading-relaxed wr-msg ${
                m.role === "user"
                  ? "bg-[var(--wr-pine)] text-white"
                  : "bg-[#121C16] text-white/90 border border-white/5"
              }`}>
                {m.content ? <MessageBody role={m.role} content={m.content} /> : (m.pending ? <Loader2 size={16} className="animate-spin" /> : "…")}
                {m.costs && <CostCard costs={m.costs} locale={locale} />}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 mt-auto pb-6 pt-3 bg-[var(--wr-night)]">
          <div className="mb-3 space-y-2">
            <ChatError locale={locale} />
            <TripPanel locale={locale} />
          </div>
          {handoffNotice && (
            <div className="mb-3 text-[12px] text-white/70 italic" data-testid="concierge-handoff-notice">
              {locale === "ro"
                ? "Mesajul este completat din WOW Journey — verifică-l înainte de a-l trimite."
                : "Message prefilled from WOW Journey — review before sending."}
            </div>
          )}
          <div className="flex items-center gap-3 border border-white/15 bg-[#121C16] rounded-md px-5 py-3 backdrop-blur-xl">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("concierge.placeholder")}
              rows={handoffNotice ? 5 : 1}
              className="flex-1 bg-transparent border-0 focus:outline-none text-[15px] text-white placeholder-white/40 py-2 resize-none"
              data-testid="concierge-page-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim()}
              className="p-2.5 rounded-md bg-[var(--wr-red)] hover:bg-[var(--wr-red-hover)] disabled:opacity-40 transition-colors"
              data-testid="concierge-page-send"
              aria-label="send"
            >
              {streaming && !input.trim() ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
